import React, { useContext, useEffect, useState } from "react";

import { graphql } from "gatsby";

import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { Blurred } from "../Blur";
import Columns from "../Columns";
import Headline from "../../../components/base/atoms/Headline";
import Icon, { IconPredefined } from "../icons/Icon";
import JsonLd from "../JsonLd";
import LinkComponent, { Link as ExternalLink, Link } from "../LinkComponent";
import useStore from "../Navigation/ContactForm/store";
import TrackableContent from "../TrackableContent";
import SearchBar from "../SearchBar/SearchBar";
import { CollapsibleContent, CollapsibleTitle } from "../Collapsible";
import CollapsibleGroup from "../CollapsibleGroup";
import { useLocation } from "@gatsbyjs/reach-router";

const Wrapper = styled(Blurred)`
    position: relative;
    display: flex;
    min-height: 200px;
    flex-grow: 50;
    padding-top: 40px;
    padding-bottom: 40px;
    color: #05235f !important;
    background-color: #e1e1e1;

    .legacyPage & {
        font-family: var(--font-family-vito);
    }
`;

export const FooterContainer = styled.div`
    flex-grow: 50;
    ${({ theme }) => theme.paddings.containerOuter.left}
    ${({ theme }) => theme.paddings.containerOuter.right}
    padding-top: 40px;
    padding-bottom: 40px;
    color: #05235f;
    background-color: #e1e1e1;

    ${({ theme }) => theme.breakpoints.upTo.large.css`
        & > div > div:not(:first-of-type) {
            margin-left: 22px;
        }
    `}

    ${({ theme }) => theme.breakpoints.upTo.medium.css`

    & > div > div:not(:first-of-type) {
    margin-left: 16px;
    }


  `}
    ${({ theme }) => theme.breakpoints.upTo.small.css`
    & > div > div:not(:first-of-type) {
    margin-left: 0px;
  }
  `}
    ${({ theme }) => theme.breakpoints.upTo.extraSmall.css`

    & > div > div:not(:first-of-type) {
    margin-left: 0px;
  }
  `}
    ${({ theme }) => theme.breakpoints.from.large.css`
    & > div > div:not(:first-of-type) {
    margin-left: 28px;
  }


`}
`;

const FlexWrapper = styled.div`
    display: flex;
    justify-content: space-between;
`;

const StyledHeadline = styled(Headline)`
    font-weight: 900 !important;
    font-size: 20px !important;
    color: #05235f;
`;

const FooterColumnContent = styled.div`
    font-size: 16px;
    font-weight: 400;

    p {
        margin-bottom: 0 !important;
    }
`;

const FooterLinkList = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    font-weight: 400;

    a {
        color: #05235f;
        text-decoration: underline;

        :hover {
            color: #05235f;
        }
    }
`;

const contactLink = css`
    margin-top: 40px;
    font-size: 16px !important;
    font-weight: 400;
    margin-bottom: 10px;
`;

const ContentAndFooter = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const Content = styled.div`
    flex-grow: 1;
    font-weight: 400;
`;

const StyledLink = styled.a`
    color: #05235f;
    text-decoration: underline;
    font-weight: 400;

    :hover {
        color: #05235f;
    }
`;
const StyledLinkComponent = styled(LinkComponent)`
    color: #05235f !important;
    text-decoration: underline !important;
    font-weight: 400;

    :hover {
        color: #05235f;
    }
`;

const SocialIconWrapper = styled.div`
    color: #05235f;
    display: flex;
    column-gap: 10px;
    ${({ theme }) => theme.breakpoints.upTo.medium.css`
         width: 100%;
         margin-bottom: 45px;


    `}
`;
const StyledCollapsibleTitle = styled(CollapsibleTitle)`
    display: flex;
    justify-content: space-between;
    width: 100%;

    ${({ theme }) => theme.breakpoints.upTo.medium.css`
    &[aria-expanded="true"] {
      text-decoration: underline solid 2px;
    }
    &[aria-expanded="false"] {
      text-decoration: none;
    }
  `}
`;

const isExternalLink = (link) => link && !link.startsWith("/");
const getHttpsLink = (link) => {
    return link && link.startsWith("www.") ? `https://${link}` : link;
};

const Meta = ({ name, address, url, email, telephone }) => (
    <JsonLd>
        {{
            "@context": "https://schema.org/",
            "@id": `https://www.hoyer.de/impressum#${encodeURIComponent(name)}`,
            "@type": "Corporation",
            name,
            address: {
                "@type": "PostalAddress",
                ...address,
            },
            url,
            email,
            telephone,
        }}
    </JsonLd>
);

const Footer = ({ footer }) => {
    const { setContactFormVisible } = useStore();

    const [isMobile, setIsMobile] = useState(false);

    const location = useLocation();
    const hideElement = location.pathname === "/tankstellen/finder/";

    useEffect(() => {
        if (typeof window === "undefined") return;
        const handleResize = () => setIsMobile(window.innerWidth < 1264);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const renderItem = (item) => {
        {
            if (item.href.includes("news.hoyer.de")) {
                return (
                    <a
                        key={item.text}
                        href={item.href}
                        role="button"
                        onClick={(e) => {
                            e.preventDefault();
                        }}
                    >
                        {item.text}
                    </a>
                );
            }
        }

        if (item.href.includes("#cookieConsent")) {
            return (
                <a
                    key={item.text}
                    role="button"
                    href={item.href}
                    onClick={(e) => {
                        e.preventDefault();
                        window.CCM?.openWidget?.();
                    }}
                >
                    {item.text}
                </a>
            );
        }

        return isExternalLink(item.href) ? (
            <ExternalLink key={item.text} to={item.href} target="_blank">
                {item.text}
            </ExternalLink>
        ) : (
            <Link key={item.text} to={item.href}>
                {item.text}
            </Link>
        );
    };

    return (
        <>
            <Wrapper eles={["Footer"]} id="footer">
                <Meta {...footer?.contact?.organization} />
                <TrackableContent name="footer">
                    <FooterContainer>
                        <FlexWrapper>
                            <div>
                                <StyledHeadline lookLike="4">
                                    {footer?.contact?.name}
                                </StyledHeadline>
                                <FooterColumnContent>
                                    <p>{footer?.contact?.organization.name}</p>
                                    <p>
                                        {
                                            footer?.contact?.organization
                                                .address.streetAddress
                                        }
                                    </p>
                                    <p>
                                        {
                                            footer?.contact?.organization
                                                .address.postalCode
                                        }{" "}
                                        {
                                            footer?.contact?.organization
                                                .address.locality
                                        }
                                    </p>
                                    <p>
                                        {
                                            footer?.contact?.organization
                                                .address.country
                                        }
                                    </p>
                                    <p>
                                        <StyledLink
                                            href={getHttpsLink(
                                                footer?.contact?.organization
                                                    .url
                                            )}
                                        >
                                            {footer?.contact?.organization.url}
                                        </StyledLink>
                                    </p>
                                </FooterColumnContent>
                                <StyledLinkComponent
                                    noLink
                                    icon="arrow-right"
                                    css={contactLink}
                                    link={{
                                        text: footer?.contact?.formLinkText,
                                    }}
                                    onClick={() => setContactFormVisible(true)}
                                />
                            </div>
                            {!isMobile &&
                                footer?.sections.map((section) => (
                                    <div key={`section_${section.name}`}>
                                        <StyledHeadline lookLike="4">
                                            {section.name}
                                        </StyledHeadline>
                                        <FooterColumnContent>
                                            <FooterLinkList>
                                                {section.items.map((item) =>
                                                    renderItem(item)
                                                )}
                                            </FooterLinkList>
                                        </FooterColumnContent>
                                    </div>
                                ))}
                        </FlexWrapper>

                        {isMobile && (
                            <>
                                <SocialIconWrapper>
                                    {footer?.contact?.organization.socialMedia.map(
                                        (item, index) => (
                                            <ExternalLink
                                                key={index}
                                                to={item.href}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <Icon
                                                    iconImg={
                                                        item.image.src.publicURL
                                                    }
                                                    altText={item.name}
                                                />
                                            </ExternalLink>
                                        )
                                    )}
                                </SocialIconWrapper>

                                <CollapsibleGroup
                                    initial={undefined}
                                    collapsibleStyle={{
                                        border: "none",
                                    }}
                                >
                                    {footer?.sections.map((section, i) => (
                                        <CollapsibleGroup.Item
                                            key={i}
                                            name={`footer-${section.name
                                                .replace(/\s+/g, "-")
                                                .toLowerCase()}`}
                                            role="group"
                                        >
                                            <StyledCollapsibleTitle
                                                contentId={i}
                                            >
                                                {section.name}
                                            </StyledCollapsibleTitle>
                                            <CollapsibleContent>
                                                <FooterLinkList>
                                                    {section.items.map((item) =>
                                                        renderItem(item)
                                                    )}
                                                </FooterLinkList>
                                            </CollapsibleContent>
                                        </CollapsibleGroup.Item>
                                    ))}
                                </CollapsibleGroup>
                            </>
                        )}

                        {!isMobile && (
                            <SocialIconWrapper>
                                {footer?.contact?.organization.socialMedia.map(
                                    (item, index) => (
                                        <ExternalLink
                                            key={index}
                                            to={item.href}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <Icon
                                                iconImg={
                                                    item.image.src.publicURL
                                                }
                                                altText={item.name}
                                            />
                                        </ExternalLink>
                                    )
                                )}
                            </SocialIconWrapper>
                        )}
                    </FooterContainer>
                </TrackableContent>
            </Wrapper>
            {!hideElement && <SearchBar />}
        </>
    );
};

export default Footer;

export const WithFooter = ({ children, ...props }) => (
    <ContentAndFooter>
        <TrackableContent name="content">
            <Content>{children}</Content>
        </TrackableContent>
        <Footer {...props} />
    </ContentAndFooter>
);

export const query = graphql`
    fragment Footer on FootersYaml {
        contact {
            organization {
                name
                address {
                    streetAddress
                    postalCode
                    locality
                    country
                }
                url
                email
                socialMedia {
                    name
                    href
                    image {
                        src {
                            publicURL
                        }
                    }
                }
                telephone
            }
            name
            formLinkText
        }
        sections {
            name
            items {
                text
                href
            }
        }
    }
`;
